import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import CardComponent from '../../../components/CardComponent/CardComponent';
import React, { useEffect, useState } from 'react';
import {
  ISipClientHistoryFilterParams,
  ISipClientHistoryProps,
} from '../../../services/SipClientService/types';
import { IPaginationParams } from '../../../services/types';
import { Box, CircularProgress } from '@mui/material';
import {
  AudioFile,
  Audiotrack,
  Block,
  CallEnd,
  CallMade,
  CallMissed,
  CallReceived,
  Check,
  FilterAlt,
  FilterAltOff,
  MusicVideo,
  VideoCall,
} from '@mui/icons-material';
import TooltipComponent from '../../../components/TooltipComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { getStreamByEndpointAsync } from '../../../services/Events';
import { ENDPOINT_SIP_CLIENT_HISTORY } from '../../../services/SipClientService';
import { useGetAllSipClientHistoryByLicenseCountAsync } from '../../../services/SipClientService/hooks/useMutation';
import SipVideoHistoryViewModal from './SipVideoHistoryViewModal';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import SipClientFillingReasonModal from '../Reason/SipClientFillingReasonModal';
import SipVideoHistoryFilterModal from './SipVideoHistoryFilterModal';

const SipVideoHistoryManagement = () => {
  const [isVideoToViewInModal, setIsVideoToViewInModal] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [selectedHistoryId, setSelectedHistoryId] = useState<
    number | undefined
  >();
  const [
    isOpenSipClientFillingReasonModal,
    setIsOpenSipClientFillingReasonModal,
  ] = useState(false);
  const [
    isVisibleSipClientVideoHistoryModal,
    setIsVisibleSipClientVideoHistoryModal,
  ] = useState(false);
  const [sipClientHistories, setSipClientHistories] = useState<
    ISipClientHistoryProps[]
  >([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);
  const [filter, setFilter] = useState<ISipClientHistoryFilterParams>(
    {} as ISipClientHistoryFilterParams,
  );
  const [sipClientHistoriesCount, setSipClientHistoriesCount] = useState(0);

  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });

  const { mutateAsync: mutateGetAllSipClientHistoryByLicenseCountAsync } =
    useGetAllSipClientHistoryByLicenseCountAsync();

  const historyColumns: IColumnProps[] = [
    {
      label: 'De',
      id: 'from',
    },
    {
      label: 'Para',
      id: 'to',
    },
    {
      label: 'Situação',
      id: 'callType',
      render(type) {
        switch (type) {
          case 'RECEIVED':
            return (
              <Box display={'flex'} alignItems={'center'} gap={0.5}>
                <CallReceived fontSize={'small'} color={'success'} />
                Recebida
              </Box>
            );
          case 'DONE':
            return (
              <Box display={'flex'} alignItems={'center'} gap={0.5}>
                <CallMade fontSize={'small'} color={'primary'} />
                Efetuada
              </Box>
            );
          case 'REFUSED':
            return (
              <Box display={'flex'} alignItems={'center'} gap={0.5}>
                <CallEnd fontSize={'small'} color={'error'} />
                Recusada
              </Box>
            );
          default:
            return (
              <Box display={'flex'} alignItems={'center'} gap={0.5}>
                <CallMissed fontSize={'small'} color={'error'} />
                Perdida
              </Box>
            );
        }
      },
    },
    {
      label: 'Duração',
      id: 'secondsDuration',
      render(seg: string) {
        const formatDuration = (totalSeconds: number) => {
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`;
          }
          if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
          }
          return `${seconds}s`;
        };

        return <>{formatDuration(Number(seg))}</>;
      },
    },
    {
      label: 'Horário',
      id: 'happenedAt',
      render(val) {
        const date = new Date(val);
        return <>{date.toLocaleString('pt-BR')}</>;
      },
    },
    {
      label: 'Justificativa',
      id: 'id',
      render(id) {
        const sipHistory = sipClientHistories.find((s) => s.id == Number(id));

        if (!sipHistory || !sipHistory.reason) return <>Sem justificativa</>;
        return (
          <Box display={'flex'} justifyContent={'space-between'}>
            <TooltipComponent title={sipHistory?.reasonDetails ?? ''}>
              {sipHistory.reason.description}
            </TooltipComponent>
          </Box>
        );
      },
    },
    {
      label: 'Status',
      id: 'status',
      render(val) {
        let render = <></>;

        if (val == 'READY')
          render = (
            <>
              <Check color={'success'} />
              Pronto
            </>
          );
        else {
          render = (
            <>
              <CircularProgress size={18} />
              Baixando áudio/vídeo
            </>
          );
        }
        return (
          <Box display={'flex'} gap={1} alignItems={'center'}>
            {render}
          </Box>
        );
      },
    },
    {
      label: 'Tipo de gravação',
      id: 'id',
      render: (id) => {
        let render = <></>;
        const sipHistory = sipClientHistories.find((s) => s.id == Number(id));

        if (sipHistory) {
          if (sipHistory.videoUrl)
            render = (
              <>
                <MusicVideo />
                ÁUDIO/VÍDEO
              </>
            );
          else if (sipHistory.audioUrl)
            render = (
              <>
                <AudioFile />
                SOMENTE ÁUDIO
              </>
            );
          else
            render = (
              <>
                <Block />
                NENHUMA GRAVAÇÃO
              </>
            );
        }
        return (
          <Box display={'flex'} gap={1} alignItems={'center'}>
            {render}
          </Box>
        );
      },
    },

    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: 0,
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          isWithDefaultOptionsList
          editButtonLabel={'Editar motivo'}
          actionOptions={actionOptions(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const actionOptions = (id: number) => {
    const sipHistory = sipClientHistories.find((s) => s.id == Number(id));
    if (!sipHistory) return [];

    const options = [];

    if (sipHistory.videoUrl || sipHistory.audioUrl) {
      options.push({
        label: sipHistory.videoUrl
          ? 'Ver gravação'
          : sipHistory.audioUrl
            ? 'Ouvir áudio'
            : 'Não habilitado',
        icon: sipHistory.videoUrl ? (
          <VideoCall />
        ) : sipHistory.audioUrl ? (
          <Audiotrack />
        ) : (
          <></>
        ),
        disabled: !sipHistory.videoUrl && !sipHistory.audioUrl,
        onClick: () => {
          setIsVideoToViewInModal(sipHistory.videoUrl != undefined);
          setSelectedVideoUrl(sipHistory.videoUrl ?? sipHistory.audioUrl!);
          setIsVisibleSipClientVideoHistoryModal(true);
        },
      });
    }
    return options;
  };

  const handleEdit = (id: number) => {
    setSelectedHistoryId(id);
    setIsOpenSipClientFillingReasonModal(true);
  };

  const getSipClientHistories = (
    pagination: IPaginationParams,
    filter: ISipClientHistoryFilterParams,
  ) => {
    getStreamByEndpointAsync(
      ENDPOINT_SIP_CLIENT_HISTORY,
      setSipClientHistories,
      pagination,
      undefined,
      undefined,
      undefined,
      true,
      filter,
    );
    mutateGetAllSipClientHistoryByLicenseCountAsync(filter).then(
      setSipClientHistoriesCount,
    );
    
  };

  useEffect(() => {
    getSipClientHistories(pagination, filter);
  }, [pagination]);

  return (
    <>
      <CardComponent title="HISTÓRICO DE CHAMADAS">
        <TableComponent
          totalCount={sipClientHistoriesCount}
          isPaginated
          rows={sipClientHistories}
          onChangePageOrRowsPerPage={setPagination}
          columns={historyColumns}
          additionalTitleComponent={
            <Box display="flex" gap={2}>
              <ButtonComponent
                startIcon={<FilterAlt />}
                size={'small'}
                onClick={() => setIsOpenFilterModal(true)}
              >
                Filtrar
              </ButtonComponent>
              <ButtonComponent
                startIcon={<FilterAltOff />}
                size={'small'}
                color={'secondary'}
                onClick={() => {
                  setFilter({});
                  getSipClientHistories(pagination, {});
                }}
              >
                Limpar Filtro
              </ButtonComponent>
            </Box>
          }
        />
      </CardComponent>

      <SipVideoHistoryViewModal
        isVideo={isVideoToViewInModal}
        isVisible={isVisibleSipClientVideoHistoryModal}
        onClose={() => {
          setIsVisibleSipClientVideoHistoryModal(false);
          setSelectedVideoUrl('');
        }}
        videoUrl={selectedVideoUrl}
      />

      <SipVideoHistoryFilterModal
        onClose={() => {
          setIsOpenFilterModal(false);
          setFilter({});
        }}
        setFilter={setFilter}
        filter={filter}
        handleFilter={() => {
          getSipClientHistories(pagination, filter);
          setIsOpenFilterModal(false);
        }}
        isOpen={isOpenFilterModal}
      />

      {selectedHistoryId && (
        <SipClientFillingReasonModal
          isEdit
          onClose={() => {
            setSelectedHistoryId(undefined);
            setIsVisibleSipClientVideoHistoryModal(false);
          }}
          isOpen={isOpenSipClientFillingReasonModal}
          lastSipClientHistoryId={selectedHistoryId}
        />
      )}
    </>
  );
};

export default SipVideoHistoryManagement;
